import { useEffect, useRef, useState, type ChangeEvent, type Dispatch, type SetStateAction } from "react";
import {
  type MomentButton,
  type MomentCarouselItem,
  type MomentCommerceIntro,
  type MomentCoupon,
  type MomentImageAsset,
  type MomentListItem,
  type MomentMoreButton,
  type MomentMessageType,
  createMomentButton,
  createMomentCarouselItem,
  createMomentCommerceIntro,
  createMomentCoupon,
  createMomentImageAsset,
  createMomentListItem,
  createMomentMoreButton,
  getMaxButtons,
  MOMENT_MESSAGE_TYPE_OPTIONS,
} from "../data/momentOptions";
import type { MomentAdDto, MomentAdDetailDto } from "../api/momentApi";
import { getDraftMessageAd } from "../api/momentApi";
import {
  toStringValue,
  toBooleanValue,
  parseButtons,
  parseCoupon,
  parseListItems,
  parseCarouselItems,
  parseCommerceIntro,
} from "../utils/messageParsers";

export function useMomentForm(channelLabel: string) {
  // ── 메시지 작성 상태 ──
  const [messageName, setMessageName] = useState("");
  const [channel, setChannel] = useState(channelLabel);
  const [messageType, setMessageType] = useState<MomentMessageType>("BASIC_TEXT_MESSAGE");
  const [messageBody, setMessageBody] = useState("");
  const [messageTitle, setMessageTitle] = useState("");
  const [wideImage, setWideImage] = useState<MomentImageAsset>(createMomentImageAsset());
  const [landingPcUrl, setLandingPcUrl] = useState("");
  const [landingMobileUrl, setLandingMobileUrl] = useState("");
  const [buttons, setButtons] = useState<MomentButton[]>([createMomentButton("button-1")]);
  const [ageAuthEnabled, setAgeAuthEnabled] = useState(false);
  const [adFlag, setAdFlag] = useState(true);
  const [shareFlag, setShareFlag] = useState(false);
  const [couponEnabled, setCouponEnabled] = useState(false);
  const [coupon, setCoupon] = useState<MomentCoupon>(createMomentCoupon());
  const [moreButtonEnabled, setMoreButtonEnabled] = useState(false);
  const [moreButton, setMoreButton] = useState<MomentMoreButton>(createMomentMoreButton());
  const [listItems, setListItems] = useState<MomentListItem[]>([
    createMomentListItem("list-1"),
    createMomentListItem("list-2"),
    createMomentListItem("list-3"),
  ]);
  const [carouselItems, setCarouselItems] = useState<MomentCarouselItem[]>([
    createMomentCarouselItem("carousel-1"),
    createMomentCarouselItem("carousel-2"),
  ]);
  const [commerceIntroEnabled, setCommerceIntroEnabled] = useState(false);
  const [commerceIntro, setCommerceIntro] = useState<MomentCommerceIntro>(createMomentCommerceIntro());
  const imageUrlRef = useRef<string | null>(null);

  // ── 버튼/타이틀 토글 (외부 상태로 끌어올림) ──
  const [buttonsEnabled, setButtonsEnabled] = useState(false);
  const [titleEnabled, setTitleEnabled] = useState(false);

  // ── API 연동 상태 ──
  const [savedAd, setSavedAd] = useState<MomentAdDto | null>(null);
  const [originalMessageType, setOriginalMessageType] = useState<MomentMessageType | null>(null);
  const [isLoadedDraft, setIsLoadedDraft] = useState(false);

  useEffect(() => {
    return () => {
      if (imageUrlRef.current) URL.revokeObjectURL(imageUrlRef.current);
    };
  }, []);

  const selectedType =
    MOMENT_MESSAGE_TYPE_OPTIONS.find((o) => o.value === messageType) ??
    MOMENT_MESSAGE_TYPE_OPTIONS[0];

  const effectiveMaxButtons = getMaxButtons(messageType);

  // ── 유형 전환 통합 핸들러 ──
  const handleMessageTypeChange = (value: MomentMessageType) => {
    if (isLoadedDraft) return;

    const prevSupportsTitle = messageType === "WIDE_MESSAGE" || messageType === "WIDE_LIST_MESSAGE";
    const nextSupportsTitle = value === "WIDE_MESSAGE" || value === "WIDE_LIST_MESSAGE";

    setMessageType(value);

    // messageTitle / titleEnabled
    if (!nextSupportsTitle) {
      setMessageTitle("");
      setTitleEnabled(false);
    } else if (value === "WIDE_MESSAGE") {
      setTitleEnabled(messageTitle.trim().length > 0);
    }
    // WIDE_LIST로 가면 필수이므로 titleEnabled 무관

    // buttons / buttonsEnabled
    const prevMax = getMaxButtons(messageType);
    const newMax = getMaxButtons(value);
    if (newMax === 0) {
      // 캐러셀 등 메시지 레벨 버튼 미지원 → OFF
      setButtonsEnabled(false);
    } else if (prevMax === 0 && newMax > 0) {
      // 캐러셀 → 버튼 지원 유형으로 복귀: 기존 버튼 데이터가 있으면 자동 ON
      const hasButtonData = buttons.some(
        (b) => b.title.trim() || b.mobileLandingUrl.trim() || b.pcLandingUrl.trim()
      );
      setButtonsEnabled(hasButtonData);
    }
    // 그 외(버튼 지원 유형 ↔ 버튼 지원 유형): buttonsEnabled 그대로 유지

    // landingPcUrl (WIDE_MESSAGE 전용)
    if (value !== "WIDE_MESSAGE") setLandingPcUrl("");
    if (value !== "CAROUSEL_COMMERCE_MESSAGE") {
      setCommerceIntroEnabled(false);
      setCommerceIntro(createMomentCommerceIntro());
    }
  };

  // ── 이미지 핸들러 ──
  const handleImageChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (imageUrlRef.current) URL.revokeObjectURL(imageUrlRef.current);
    const nextUrl = URL.createObjectURL(file);
    imageUrlRef.current = nextUrl;
    setWideImage({
      imagePreview: nextUrl,
      imageName: file.name,
      imageFile: file,
      imageUploadedUrl: "",
    });
  };

  // ── 버튼 핸들러 ──
  const handleButtonChange = (id: string, key: keyof MomentButton, value: string) => {
    setButtons((prev) =>
      prev.map((button) => (button.id === id ? { ...button, [key]: value } : button))
    );
  };

  const addButton = () => {
    setButtons((prev) => {
      if (prev.length >= effectiveMaxButtons) return prev;
      return [...prev, createMomentButton(`button-${Date.now()}`)];
    });
  };

  const removeButton = (id: string) => {
    setButtons((prev) => {
      if (prev.length === 1) return [createMomentButton(prev[0].id)];
      return prev.filter((button) => button.id !== id);
    });
  };

  // ── 리스트 아이템 핸들러 ──
  const addListItem = () => {
    setListItems((prev) => {
      if (prev.length >= 5) return prev;
      return [...prev, createMomentListItem(`list-${Date.now()}`)];
    });
  };

  const removeListItem = (id: string) => {
    setListItems((prev) => {
      if (prev.length <= 3) return prev;
      return prev.filter((item) => item.id !== id);
    });
  };

  const updateImageState = <T extends {
    id: string;
    imagePreview: string | null;
    imageName: string | null;
    imageFile: File | null;
    imageUploadedUrl: string;
  }>(
    file: File | undefined,
    updater: Dispatch<SetStateAction<T[]>>,
    id: string
  ) => {
    if (!file) return;
    const nextUrl = URL.createObjectURL(file);
    updater((prev) =>
      prev.map((item) =>
        item.id === id
          ? { ...item, imagePreview: nextUrl, imageName: file.name, imageFile: file, imageUploadedUrl: "" }
          : item
      )
    );
  };

  const handleListItemImageChange = (id: string, event: ChangeEvent<HTMLInputElement>) => {
    updateImageState(event.target.files?.[0], setListItems, id);
  };

  const handleListItemChange = (
    id: string,
    key: "description" | "pcLandingUrl" | "mobileLandingUrl",
    value: string
  ) => {
    setListItems((prev) =>
      prev.map((item) => (item.id === id ? { ...item, [key]: value } : item))
    );
  };

  // ── 캐러셀 아이템 핸들러 ──
  const handleCarouselItemImageChange = (id: string, event: ChangeEvent<HTMLInputElement>) => {
    updateImageState(event.target.files?.[0], setCarouselItems, id);
  };

  const handleCommerceIntroImageChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const nextUrl = URL.createObjectURL(file);
    setCommerceIntro((prev) => ({
      ...prev,
      imagePreview: nextUrl,
      imageName: file.name,
      imageFile: file,
      imageUploadedUrl: "",
    }));
  };

  const handleCommerceIntroChange = (
    key: "messageTitle" | "description" | "pcLandingUrl" | "mobileLandingUrl",
    value: string
  ) => {
    setCommerceIntro((prev) => ({ ...prev, [key]: value }));
  };

  // ── 라이브러리 선택 핸들러 (이미 S3에 업로드된 URL을 직접 사용) ──
  const handleWideImageLibrarySelect = (imgUrl: string) => {
    if (imageUrlRef.current) URL.revokeObjectURL(imageUrlRef.current);
    imageUrlRef.current = null;
    setWideImage({
      imagePreview: imgUrl,
      imageName: imgUrl.split("/").pop() ?? imgUrl,
      imageFile: null,
      imageUploadedUrl: imgUrl,
    });
  };

  const handleListItemLibrarySelect = (id: string, imgUrl: string) => {
    setListItems((prev) =>
      prev.map((item) =>
        item.id === id
          ? { ...item, imagePreview: imgUrl, imageName: imgUrl.split("/").pop() ?? imgUrl, imageFile: null, imageUploadedUrl: imgUrl }
          : item
      )
    );
  };

  const handleCarouselItemLibrarySelect = (id: string, imgUrl: string) => {
    setCarouselItems((prev) =>
      prev.map((item) =>
        item.id === id
          ? { ...item, imagePreview: imgUrl, imageName: imgUrl.split("/").pop() ?? imgUrl, imageFile: null, imageUploadedUrl: imgUrl }
          : item
      )
    );
  };

  const handleCommerceIntroLibrarySelect = (imgUrl: string) => {
    setCommerceIntro((prev) => ({
      ...prev,
      imagePreview: imgUrl,
      imageName: imgUrl.split("/").pop() ?? imgUrl,
      imageFile: null,
      imageUploadedUrl: imgUrl,
    }));
  };

  const handleCommerceIntroEnabledChange = (value: boolean) => {
    setCommerceIntroEnabled(value);
    if (!value) {
      setCarouselItems((prev) => {
        if (prev.length >= 2) return prev;
        return [...prev, createMomentCarouselItem(`carousel-${Date.now()}`)];
      });
    }
  };

  const handleCarouselItemChange = (
    id: string,
    key:
      | "messageTitle" | "description" | "pcLandingUrl" | "mobileLandingUrl"
      | "buttonTitle" | "buttonPcLandingUrl" | "buttonMobileLandingUrl"
      | "secondaryButtonTitle" | "secondaryButtonPcLandingUrl" | "secondaryButtonMobileLandingUrl"
      | "secondaryButtonEnabled"
      | "priceAmount" | "discountedPriceAmount",
    value: string | boolean
  ) => {
    setCarouselItems((prev) =>
      prev.map((item) => (item.id === id ? { ...item, [key]: value } : item))
    );
  };

  const handleCarouselItemShareFlagChange = (id: string, value: boolean) => {
    setCarouselItems((prev) =>
      prev.map((item) =>
        item.id === id
          ? {
              ...item,
              shareFlag: value,
              ...(value
                ? {
                    secondaryButtonEnabled: false,
                    secondaryButtonTitle: "",
                    secondaryButtonPcLandingUrl: "",
                    secondaryButtonMobileLandingUrl: "",
                  }
                : {}),
            }
          : item
      )
    );
  };

  const handleCarouselItemSecondaryButtonEnabledChange = (id: string, value: boolean) => {
    setCarouselItems((prev) =>
      prev.map((item) =>
        item.id === id
          ? value
            ? { ...item, secondaryButtonEnabled: true, shareFlag: false }
            : {
                ...item,
                secondaryButtonEnabled: false,
                secondaryButtonTitle: "",
                secondaryButtonPcLandingUrl: "",
                secondaryButtonMobileLandingUrl: "",
              }
          : item
      )
    );
  };

  const handleCarouselItemCouponEnabledChange = (id: string, value: boolean) => {
    setCarouselItems((prev) =>
      prev.map((item) => (item.id === id ? { ...item, couponEnabled: value } : item))
    );
  };

  const handleCarouselItemCouponChange = (id: string, key: keyof MomentCoupon, value: string) => {
    setCarouselItems((prev) =>
      prev.map((item) =>
        item.id === id ? { ...item, coupon: { ...item.coupon, [key]: value } } : item
      )
    );
  };

  const addCarouselItem = () => {
    setCarouselItems((prev) => {
      if (prev.length >= 6) return prev;
      return [...prev, createMomentCarouselItem(`carousel-${Date.now()}`)];
    });
  };

  const removeCarouselItem = (id: string) => {
    setCarouselItems((prev) => {
      const minCount = messageType === "CAROUSEL_COMMERCE_MESSAGE" && commerceIntroEnabled ? 1 : 2;
      if (prev.length <= minCount) return prev;
      return prev.filter((item) => item.id !== id);
    });
  };

  // ── 공유하기 핸들러 (buttons.slice 제거됨 — BASIC_TEXT shareFlag는 버튼과 독립) ──
  const handleShareFlagChange = (val: boolean) => {
    if (val && ageAuthEnabled) {
      const confirmed = window.confirm("연령 인증 메시지가 해제되고 공유하기 사용으로 변경됩니다. 공유하기를 사용하시겠습니까?");
      if (!confirmed) return;
      setAgeAuthEnabled(false);
    }
    setShareFlag(val);
  };

  // ── 연령인증 핸들러 ──
  const handleAgeAuthChange = (val: boolean) => {
    if (val && shareFlag) {
      const confirmed = window.confirm("공유하기가 해제되고 연령 인증 메시지 사용으로 변경됩니다. 연령 인증 메시지를 사용하시겠습니까?");
      if (!confirmed) return;
      setShareFlag(false);
    }
    setAgeAuthEnabled(val);
  };

  // ── 임시저장 불러오기 ──
  // 공통 파싱 함수(toStringValue, toBooleanValue, parseButtons, parseCoupon,
  // parseListItems, parseCarouselItems, parseCommerceIntro)는
  // utils/messageParsers.ts에서 import합니다.
  // parseWideImage는 WIDE_MESSAGE 전용 로직으로 이 훅에서만 사용합니다.

  const parseWideImage = (message: Record<string, unknown>): MomentImageAsset => {
    const items = Array.isArray(message.items) ? (message.items as Array<Record<string, unknown>>) : [];
    const firstItem = items[0] ?? {};
    const imageUrl = toStringValue(firstItem.imageUrl);
    return {
      imagePreview: imageUrl || null,
      imageName: imageUrl ? imageUrl.split("/").pop() ?? "saved-image" : null,
      imageFile: null,
      imageUploadedUrl: imageUrl,
    };
  };

  const hydrateFromDetail = (draft: MomentAdDetailDto) => {
    const message = draft.messageBody ?? {};
    const draftType = (message.type as MomentMessageType) ?? (draft.messageType as MomentMessageType);

    setSavedAd({
      uid: draft.uid,
      kakaoAdId: draft.kakaoAdId,
      adName: draft.adName,
      messageType: draft.messageType,
      status: draft.status,
      audienceCnt: draft.audienceCnt,
      unitPrice: draft.unitPrice,
      totalAmount: draft.totalAmount,
      sendDate: draft.sendDate,
      createdAt: draft.createdAt,
      updatedAt: draft.updatedAt,
    });
    setMessageName(draft.adName);
    setMessageType(draftType);
    setOriginalMessageType(draftType);
    setIsLoadedDraft(true);
    setMessageBody(toStringValue(message.description));
    setMessageTitle(toStringValue(message.messageTitle));
    setAdFlag(true);
    setShareFlag(toBooleanValue(message.shareFlag));

    const parsedButtons = parseButtons(message.buttons);
    setButtons(parsedButtons);
    // buttonsEnabled 복원
    const hasButtonData = parsedButtons.some(
      (b) => b.title.trim() || b.mobileLandingUrl.trim() || b.pcLandingUrl.trim()
    );
    setButtonsEnabled(hasButtonData);

    setCouponEnabled(!!message.couponBook);
    setCoupon(message.couponBook ? parseCoupon(message.couponBook) : createMomentCoupon());
    setAgeAuthEnabled(toBooleanValue(message.ageAuthEnabled));
    setMoreButtonEnabled(!!message.moreButton);
    setMoreButton({
      pcLandingUrl: toStringValue(((message.moreButton as Record<string, unknown>) ?? {}).pcLandingUrl),
      mobileLandingUrl: toStringValue(((message.moreButton as Record<string, unknown>) ?? {}).mobileLandingUrl),
    });

    if (draftType === "WIDE_MESSAGE") {
      const parsedWideImage = parseWideImage(message);
      setWideImage(parsedWideImage);
      const items = Array.isArray(message.items) ? (message.items as Array<Record<string, unknown>>) : [];
      const firstItem = items[0] ?? {};
      setLandingPcUrl(toStringValue(firstItem.pcLandingUrl));
      setLandingMobileUrl(toStringValue(firstItem.mobileLandingUrl));
      // titleEnabled 복원
      const title = toStringValue(message.messageTitle);
      setTitleEnabled(title.trim().length > 0);
    } else {
      setWideImage(createMomentImageAsset());
      setLandingPcUrl("");
      setLandingMobileUrl("");
    }

    if (draftType === "WIDE_LIST_MESSAGE") {
      setListItems(parseListItems(message.items));
    } else {
      setListItems([createMomentListItem("list-1"), createMomentListItem("list-2"), createMomentListItem("list-3")]);
    }

    if (draftType === "CAROUSEL_FEED_MESSAGE" || draftType === "CAROUSEL_COMMERCE_MESSAGE") {
      setCarouselItems(parseCarouselItems(message.carousels, draftType));
      if (draftType === "CAROUSEL_COMMERCE_MESSAGE" && message.introCarousel) {
        setCommerceIntroEnabled(true);
        setCommerceIntro(parseCommerceIntro(message.introCarousel));
      } else {
        setCommerceIntroEnabled(false);
        setCommerceIntro(createMomentCommerceIntro());
      }
    } else {
      setCarouselItems([createMomentCarouselItem("carousel-1"), createMomentCarouselItem("carousel-2")]);
      setCommerceIntroEnabled(false);
      setCommerceIntro(createMomentCommerceIntro());
    }
  };

  const hydrateDraft = async (draftAdId: string) => {
    const draft = await getDraftMessageAd(draftAdId);
    hydrateFromDetail(draft);
  };

  return {
    // 상태
    messageName, messageType, messageBody, messageTitle, wideImage,
    landingPcUrl, landingMobileUrl, buttons, ageAuthEnabled, adFlag,
    shareFlag, couponEnabled, coupon, moreButton, listItems, carouselItems, commerceIntroEnabled, commerceIntro,
    moreButtonEnabled, buttonsEnabled, titleEnabled,
    channel, savedAd, originalMessageType, isLoadedDraft,
    selectedType, effectiveMaxButtons,
    // setter
    setMessageName, setMessageBody, setMessageTitle, setChannel,
    setAdFlag, setCouponEnabled, setCoupon, setLandingPcUrl, setLandingMobileUrl,
    setMoreButtonEnabled, setMoreButton, setWideImage, setListItems, setCarouselItems, setCommerceIntroEnabled, setCommerceIntro,
    setButtonsEnabled, setTitleEnabled, setSavedAd,
    // 핸들러
    handleMessageTypeChange,
    handleImageChange,
    handleWideImageLibrarySelect,
    handleButtonChange, addButton, removeButton,
    handleListItemChange, handleListItemImageChange, handleListItemLibrarySelect, addListItem, removeListItem,
    handleCarouselItemChange, handleCarouselItemImageChange, handleCarouselItemLibrarySelect,
    handleCommerceIntroImageChange, handleCommerceIntroLibrarySelect, handleCommerceIntroChange, handleCommerceIntroEnabledChange,
    handleCarouselItemShareFlagChange, handleCarouselItemSecondaryButtonEnabledChange, handleCarouselItemCouponEnabledChange,
    handleCarouselItemCouponChange, addCarouselItem, removeCarouselItem,
    handleShareFlagChange, handleAgeAuthChange,
    hydrateDraft, hydrateFromDetail,
  };
}
